import { useState } from "react";
import api, { resolveImageUrl } from "@/lib/api";
import { Plus, Layers } from "lucide-react";

/**
 * Editor library card with a "× N" community-variants badge.
 * Lazy-fetches the variants only on first hover so we don't fan out 1 request
 * per product on initial load.
 */
export default function ProductCardWithVariants({ product, onAdd }) {
  const [variants, setVariants] = useState(null); // null = not fetched yet, [] = fetched empty
  const [open, setOpen] = useState(false);

  const fetchVariants = async () => {
    if (variants !== null) return;
    try {
      const { data } = await api.get(`/products/${product.id}/variants?sort=most_used`);
      setVariants(Array.isArray(data) ? data : []);
    } catch { setVariants([]); }
  };

  const onCardClick = (e) => {
    if (open) return;  // user is browsing variants; don't add by accident
    e.stopPropagation();
    onAdd?.(product, null);
  };

  const addVariant = (e, v) => {
    e.stopPropagation();
    onAdd?.(product, v);
  };

  return (
    <div
      data-testid={`product-card-${product.id}`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("application/moodly-product", JSON.stringify(product));
        e.dataTransfer.effectAllowed = "copy";
      }}
      onMouseEnter={fetchVariants}
      onClick={onCardClick}
      className="group relative border border-[#EFEFEF] bg-white hover:border-[#333333] transition-colors cursor-pointer"
    >
      <div className="aspect-square bg-[#F2EDE3] overflow-hidden">
        <img
          src={resolveImageUrl(product.image_url)}
          alt={product.title}
          draggable={false}
          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="p-2">
        <div className="text-[11px] font-body truncate">{product.title}</div>
        <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-[#595959] truncate">{product.brand}{product.price ? ` · ${product.price}` : ""}</div>
      </div>

      <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity bg-[#333333] text-white p-1">
        <Plus className="w-3 h-3" />
      </div>

      {/* Variants badge — only renders if there's at least one community edit */}
      {variants && variants.length > 0 && (
        <button
          type="button"
          data-testid={`variants-badge-${product.id}`}
          onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
          className={`absolute top-1.5 left-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] cursor-pointer transition-colors ${open ? "bg-[#9CA47E] text-white" : "bg-white border border-[#9CA47E] text-[#9CA47E] hover:bg-[#9CA47E] hover:text-white"}`}
          title={`${variants.length} community edit${variants.length === 1 ? "" : "s"}`}
        >
          <Layers className="w-2.5 h-2.5" /> ×{variants.length}
        </button>
      )}

      {/* Variants strip */}
      {open && variants && variants.length > 0 && (
        <div
          data-testid={`variants-strip-${product.id}`}
          className="absolute left-0 right-0 bottom-full mb-1 bg-white border border-[#333333] p-2 z-20 shadow-md"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-1.5">
            <div className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#9CA47E]">◇ Community edits</div>
            <button type="button" onClick={() => setOpen(false)} className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#595959] cursor-pointer hover:text-[#333333]">close</button>
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {/* Original first */}
            <button
              type="button"
              data-testid={`variant-original-${product.id}`}
              onClick={(e) => { onAdd?.(product, null); setOpen(false); e.stopPropagation(); }}
              className="shrink-0 w-14 h-14 border border-[#333333] bg-[#F2EDE3] cursor-pointer hover:ring-2 hover:ring-[#9CA47E] transition-all relative"
              title="Original"
            >
              <img src={resolveImageUrl(product.image_url)} alt="" className="w-full h-full object-cover" />
              <span className="absolute bottom-0 left-0 right-0 bg-[#333333]/85 text-white font-mono text-[7px] uppercase tracking-widest px-1 py-[1px] text-center">orig</span>
            </button>
            {variants.map((v) => (
              <button
                key={v.id}
                type="button"
                data-testid={`variant-${v.id}`}
                onClick={(e) => { addVariant(e, v); setOpen(false); }}
                className="shrink-0 w-14 h-14 border border-[#E5E5E5] bg-white cursor-pointer hover:border-[#9CA47E] hover:ring-2 hover:ring-[#9CA47E] transition-all relative"
                title={`@${v.author_username || "?"} · used ${v.used_count} time${v.used_count === 1 ? "" : "s"}${v.note ? ` — ${v.note}` : ""}`}
              >
                <img src={resolveImageUrl(v.image_url)} alt="" className="w-full h-full object-cover" />
                {v.used_count > 0 && (
                  <span className="absolute top-0 right-0 bg-[#9CA47E] text-white font-mono text-[7px] uppercase tracking-widest px-1 py-[1px]">×{v.used_count}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
